import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Layers,
  PhoneOutgoing,
  Plus,
  RotateCcw,
  Search,
  Users,
} from "lucide-react";
import { listContactBatches, updateContactBatch } from "../workflows/api";
import { UploadContactBatchModal } from "../workflows/upload-contact-batch-modal";
import { useToast } from "../../components/ui/toast";
import { ConfirmModal } from "../../components/ui/confirm-modal";
import { Modal } from "../../components/ui/modal";
import { CopyButton } from "../../components/ui/copy-button";
import { ActionMenu } from "../../components/ui/action-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Dropdown } from "../../components/ui/dropdown";
import { cn } from "../../lib/utils";
import { crmApi } from "./api";

export function CrmBatchesPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "ready" | "rejected">("all");
  const [sort, setSort] = useState("latest");
  const [showUpload, setShowUpload] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);

  // Edit details modal state
  const [editingBatch, setEditingBatch] = useState<{ id: string; name: string; tags: string[] } | null>(null);
  const [editName, setEditName] = useState("");
  const [editTags, setEditTags] = useState("");

  // Assign campaign/agent modal state
  const [assigningBatch, setAssigningBatch] = useState<{ id: string; name: string } | null>(null);
  const [assignCampaignId, setAssignCampaignId] = useState("");
  const [assignAgentId, setAssignAgentId] = useState("");

  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: batches = [], isLoading } = useQuery({
    queryKey: ["contact-batches"],
    queryFn: listContactBatches,
  });

  const { data: campaigns = [] } = useQuery({
    queryKey: ["crm-campaigns"],
    queryFn: crmApi.campaigns,
    enabled: !!assigningBatch,
  });

  const { data: team } = useQuery({
    queryKey: ["crm-team"],
    queryFn: crmApi.team,
    enabled: !!assigningBatch,
  });

  const archiveMutation = useMutation({
    mutationFn: (batchId: string) => updateContactBatch(batchId, { archived: true }),
    onSuccess: () => {
      toast.success("Batch deleted");
      void queryClient.invalidateQueries({ queryKey: ["contact-batches"] });
      setPendingDelete(null);
    },
    onError: (err: any) => {
      toast.danger("Failed to delete", err.message);
    },
  });

  const renameMutation = useMutation({
    mutationFn: ({ batchId, name, tags }: { batchId: string; name: string; tags: string[] }) =>
      updateContactBatch(batchId, { name, tags }),
    onSuccess: (_, vars) => {
      queryClient.setQueryData<any[]>(["contact-batches"], (old = []) =>
        old.map((b) => (b.batch_id === vars.batchId ? { ...b, name: vars.name, tags: vars.tags } : b)),
      );
      void queryClient.invalidateQueries({ queryKey: ["contact-batches"] });
      void queryClient.invalidateQueries({ queryKey: ["contact-batch", vars.batchId] });
      toast.success("Batch details updated");
      setEditingBatch(null);
    },
    onError: (err: any) => {
      toast.danger("Failed to update batch", err.message);
    },
  });

  const assignMutation = useMutation({
    mutationFn: (vars: { batchId: string; campaignId: string | null; assignedAgentId: string | null }) =>
      crmApi.assignBatchLeads(vars),
    onSuccess: (res) => {
      toast.success(`Assigned ${res.updated} lead${res.updated === 1 ? "" : "s"}`);
      void queryClient.invalidateQueries({ queryKey: ["crm-leads"] });
      setAssigningBatch(null);
    },
    onError: (err: any) => {
      toast.danger("Failed to assign batch", err.message);
    },
  });

  const openAssignModal = (batch: { batch_id: string; name: string }) => {
    setAssigningBatch({ id: batch.batch_id, name: batch.name });
    setAssignCampaignId("");
    setAssignAgentId("");
  };

  const handleSaveAssign = () => {
    if (!assigningBatch || assignMutation.isPending) return;
    if (!assignCampaignId && !assignAgentId) return;
    assignMutation.mutate({
      batchId: assigningBatch.id,
      campaignId: assignCampaignId || null,
      assignedAgentId: assignAgentId || null,
    });
  };

  const openEditModal = (batch: { batch_id: string; name: string; tags?: string[] }) => {
    setEditingBatch({
      id: batch.batch_id,
      name: batch.name,
      tags: batch.tags || [],
    });
    setEditName(batch.name);
    setEditTags((batch.tags || []).join(", "));
  };

  const handleSaveEdit = () => {
    if (!editingBatch || !editName.trim() || renameMutation.isPending) return;
    const parsedTags = Array.from(
      new Set(editTags.split(",").map((t) => t.trim()).filter(Boolean)),
    ).slice(0, 20);
    renameMutation.mutate({
      batchId: editingBatch.id,
      name: editName.trim(),
      tags: parsedTags,
    });
  };

  const crmBatches = useMemo(() => {
    return batches.filter((b: any) => !b.archived && b.source === "crm");
  }, [batches]);

  // Executive KPI summary metrics
  const summaryKpis = useMemo(() => {
    const totalBatches = crmBatches.length;
    let totalValid = 0;
    let totalRejected = 0;

    for (const b of crmBatches) {
      totalValid += b.valid_contacts || 0;
      totalRejected += b.rejected_contacts || 0;
    }

    const totalContacts = totalValid + totalRejected;
    const validRate = totalContacts > 0 ? Math.round((totalValid / totalContacts) * 100) : 100;

    return {
      totalBatches,
      totalContacts,
      totalValid,
      totalRejected,
      validRate,
    };
  }, [crmBatches]);

  // Search, filter, and sorting
  const needle = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    let result = crmBatches;

    // Search query
    if (needle) {
      result = result.filter((b: any) => {
        const name = String(b.name || "").toLowerCase();
        const id = String(b.batch_id || "").toLowerCase();
        const tagMatch = (b.tags || []).some((tag: string) => tag.toLowerCase().includes(needle));
        return name.includes(needle) || id.includes(needle) || tagMatch;
      });
    }

    // Status filter
    if (statusFilter === "ready") {
      result = result.filter((b: any) => (b.valid_contacts || 0) > 0);
    } else if (statusFilter === "rejected") {
      result = result.filter((b: any) => (b.rejected_contacts || 0) > 0);
    }

    // Sort order
    return [...result].sort((a: any, b: any) => {
      if (sort === "latest") {
        return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
      }
      if (sort === "oldest") {
        return new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
      }
      if (sort === "most_contacts") {
        const totalA = (a.valid_contacts || 0) + (a.rejected_contacts || 0);
        const totalB = (b.valid_contacts || 0) + (b.rejected_contacts || 0);
        return totalB - totalA;
      }
      if (sort === "name_asc") {
        return String(a.name || "").localeCompare(String(b.name || ""));
      }
      return 0;
    });
  }, [crmBatches, needle, statusFilter, sort]);

  return (
    <div className="flex h-full flex-col p-4 sm:p-6 space-y-4">
      {/* 1. Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-base font-semibold text-ink dark:text-white">Batches</h1>
        <button
          onClick={() => setShowUpload(true)}
          className="flex items-center gap-1.5 rounded-lg bg-brand px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-brand-hover active:scale-[0.98] transition-all"
        >
          <Plus className="h-3.5 w-3.5" />
          Upload Batch
        </button>
      </div>

      {/* 2. Top Summary KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {/* Total Batches */}
        <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 shadow-card dark:shadow-card-dark">
          <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
            <span className="font-medium">Total Batches</span>
            <Layers className="h-3.5 w-3.5 text-brand" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-xl font-bold text-ink dark:text-white">{summaryKpis.totalBatches}</span>
            <span className="text-xs text-ink-3 dark:text-stone-400">batches</span>
          </div>
        </div>

        {/* Total Contacts */}
        <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 shadow-card dark:shadow-card-dark">
          <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
            <span className="font-medium">Total Contacts</span>
            <Users className="h-3.5 w-3.5 text-sky-500" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-xl font-bold text-ink dark:text-white">{summaryKpis.totalContacts.toLocaleString()}</span>
            <span className="text-xs text-ink-3 dark:text-stone-400">leads</span>
          </div>
        </div>

        {/* Ready to Dial */}
        <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 shadow-card dark:shadow-card-dark">
          <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
            <span className="font-medium">Ready to Dial</span>
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{summaryKpis.totalValid.toLocaleString()}</span>
            <span className="text-xs text-emerald-600/80 dark:text-emerald-400/80 font-medium">({summaryKpis.validRate}%)</span>
          </div>
        </div>

        {/* Needs Review */}
        <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 shadow-card dark:shadow-card-dark">
          <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
            <span className="font-medium">Needs Review</span>
            <AlertCircle className="h-3.5 w-3.5 text-rose-500" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-xl font-bold text-ink dark:text-white">{summaryKpis.totalRejected}</span>
            <span className="text-xs text-rose-600 dark:text-rose-400 font-semibold">
              {summaryKpis.totalRejected > 0 ? "rejected" : "all clean"}
            </span>
          </div>
        </div>
      </div>

      {/* 3. Search, Filter & Sort Strip */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3 shadow-card dark:shadow-card-dark">
        <div className="relative min-w-[220px] flex-1 max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3 dark:text-stone-400" />
          <input
            type="text"
            placeholder="Search batches by name, id, or tag..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-line dark:border-white/20 bg-stone-100 dark:bg-stone-800 py-1.5 pl-9 pr-3 text-xs sm:text-sm text-ink dark:text-stone-100 placeholder:text-ink-faint dark:placeholder:text-stone-400 outline-none focus:border-brand"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Status Filter Segmented Control */}
          <div className="flex items-center rounded-lg border border-line dark:border-white/15 bg-stone-100 dark:bg-stone-800 p-0.5 text-xs font-medium">
            <button
              onClick={() => setStatusFilter("all")}
              className={cn(
                "rounded-md px-2.5 py-1 transition-all",
                statusFilter === "all"
                  ? "bg-white dark:bg-stone-700 text-ink dark:text-white shadow-xs font-semibold"
                  : "text-ink-3 dark:text-stone-400 hover:text-ink dark:hover:text-white",
              )}
            >
              All
            </button>
            <button
              onClick={() => setStatusFilter("ready")}
              className={cn(
                "rounded-md px-2.5 py-1 transition-all",
                statusFilter === "ready"
                  ? "bg-white dark:bg-stone-700 text-ink dark:text-white shadow-xs font-semibold"
                  : "text-ink-3 dark:text-stone-400 hover:text-ink dark:hover:text-white",
              )}
            >
              Ready to Dial
            </button>
            <button
              onClick={() => setStatusFilter("rejected")}
              className={cn(
                "rounded-md px-2.5 py-1 transition-all",
                statusFilter === "rejected"
                  ? "bg-white dark:bg-stone-700 text-ink dark:text-white shadow-xs font-semibold"
                  : "text-ink-3 dark:text-stone-400 hover:text-ink dark:hover:text-white",
              )}
            >
              Has Issues
            </button>
          </div>

          {/* Sort Dropdown */}
          <Dropdown
            ariaLabel="Sort batches"
            value={sort}
            onChange={setSort}
            className="w-36 text-xs py-1.5 bg-stone-100 dark:bg-stone-800 border-line dark:border-white/15 text-ink dark:text-stone-200"
            options={[
              { value: "latest", label: "Latest created" },
              { value: "oldest", label: "Oldest created" },
              { value: "most_contacts", label: "Most contacts" },
              { value: "name_asc", label: "Name (A–Z)" },
            ]}
          />

          {/* Reset Filters */}
          {(search || statusFilter !== "all" || sort !== "latest") && (
            <button
              title="Reset filters"
              aria-label="Reset filters"
              onClick={() => {
                setSearch("");
                setStatusFilter("all");
                setSort("latest");
              }}
              className="rounded-lg border border-line dark:border-white/15 bg-stone-100 dark:bg-stone-800 p-1.5 text-xs text-ink-3 dark:text-stone-400 hover:text-ink dark:hover:text-white transition-colors"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          )}

          <span className="text-xs font-medium text-ink-3 dark:text-stone-400 pl-1">
            {filtered.length} of {crmBatches.length} batches
          </span>
        </div>
      </div>

      {/* 4. Table / Empty States */}
      {isLoading ? (
        <div className="p-16 text-center text-sm text-ink-3 dark:text-stone-400">Loading batches...</div>
      ) : crmBatches.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-line dark:border-white/15 bg-stone-50 dark:bg-stone-900/30 py-24 text-center">
          <Users className="mb-4 h-12 w-12 text-ink-3 dark:text-stone-500" />
          <h3 className="mb-2 text-lg font-semibold text-ink dark:text-stone-100">No batches yet</h3>
          <p className="mb-6 max-w-md text-sm text-ink-3 dark:text-stone-400">
            Select leads or customers on the Leads/Customers page and use "Create batch", or add contacts directly here.
          </p>
          <button
            onClick={() => setShowUpload(true)}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-xs hover:bg-brand-hover transition-colors"
          >
            Add your first batch
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-line dark:border-white/15 bg-elevated dark:bg-[#201e1b] p-16 text-center">
          <Search className="mx-auto mb-3 h-10 w-10 text-ink-3 dark:text-stone-500" />
          <h3 className="text-sm font-bold text-ink dark:text-stone-100">No batches match that filter</h3>
          <p className="mt-1 text-xs text-ink-3 dark:text-stone-400">Try adjusting your search terms or filters.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#201e1b] shadow-xs">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 z-10 bg-stone-100 dark:bg-stone-800/80 text-[11px] font-semibold uppercase tracking-wider text-ink-2 dark:text-stone-300 border-b border-line dark:border-white/15">
              <tr>
                <th className="px-4 py-3">Batch Name</th>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Contacts</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line dark:divide-white/10">
              {filtered.map((batch: any) => {
                const total = (batch.valid_contacts || 0) + (batch.rejected_contacts || 0);

                return (
                  <tr
                    key={batch.batch_id}
                    onClick={() => navigate(`/crm/batches/${batch.batch_id}`)}
                    className="group cursor-pointer hover:bg-stone-50 dark:hover:bg-stone-800/50 transition-colors"
                  >
                    {/* Batch Name & Tags */}
                    <td className="px-4 py-3.5 align-middle">
                      <div className="min-w-0">
                        <p className="font-semibold text-ink dark:text-stone-100 group-hover:text-brand transition-colors">
                          {batch.name}
                        </p>
                        {(batch.tags || []).length > 0 && (
                          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                            {batch.tags.map((tag: string) => (
                              <span
                                key={tag}
                                className="inline-flex items-center rounded-md bg-stone-100 dark:bg-stone-800 px-2 py-0.5 text-[11px] font-medium text-stone-600 dark:text-stone-300 border border-stone-200/60 dark:border-white/10"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* ID */}
                    <td className="px-4 py-3.5 align-middle">
                      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <span className="font-mono text-xs text-ink-3 dark:text-stone-400">{batch.batch_id}</span>
                        <CopyButton value={batch.batch_id} />
                      </div>
                    </td>

                    {/* Contacts Breakdown */}
                    <td className="px-4 py-3.5 align-middle">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                            {batch.valid_contacts || 0} ready
                          </span>
                          {(batch.rejected_contacts || 0) > 0 && (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/50 px-2.5 py-0.5 text-xs font-semibold text-rose-700 dark:text-rose-300">
                              <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
                              {batch.rejected_contacts} rejected
                            </span>
                          )}
                        </div>
                        <span className="text-[11px] text-ink-3 dark:text-stone-400">
                          {total} total contact{total === 1 ? "" : "s"}
                        </span>
                      </div>
                    </td>

                    {/* Created */}
                    <td className="px-4 py-3.5 align-middle text-xs text-ink-3 dark:text-stone-400">
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5 text-stone-400" />
                        <span>
                          {batch.created_at
                            ? formatDistanceToNow(new Date(batch.created_at), { addSuffix: true })
                            : "Recently"}
                        </span>
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3.5 align-middle">
                      <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                        {/* Primary Schedule Button: choose Workflow or WhatsApp Campaign */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1.5 rounded-lg bg-brand hover:bg-brand-hover text-white px-3 py-1.5 text-xs font-semibold shadow-xs active:scale-[0.98] transition-all"
                              title="Schedule"
                            >
                              <PhoneOutgoing className="h-3.5 w-3.5" />
                              <span>Schedule</span>
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-56 rounded-xl border border-line bg-elevated shadow-lg p-1.5 z-50">
                            <DropdownMenuItem
                              onClick={(e) => { e.stopPropagation(); navigate(`/home/automation/schedule?batchId=${encodeURIComponent(batch.batch_id)}`); }}
                              className="flex w-full items-center gap-2.5 px-3 py-2 cursor-pointer rounded-lg text-sm text-ink-2 hover:bg-soft hover:text-ink transition-colors"
                            >
                              Schedule Workflow
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => { e.stopPropagation(); navigate(`/whatsapp/campaigns?batchId=${encodeURIComponent(batch.batch_id)}`); }}
                              className="flex w-full items-center gap-2.5 px-3 py-2 cursor-pointer rounded-lg text-sm text-ink-2 hover:bg-soft hover:text-ink transition-colors"
                            >
                              Schedule WhatsApp Campaign
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>

                        {/* View Contacts Link Button */}
                        <button
                          onClick={() => navigate(`/crm/batches/${batch.batch_id}`)}
                          className="inline-flex items-center gap-1 rounded-lg border border-line dark:border-white/15 bg-white dark:bg-stone-800 px-2.5 py-1.5 text-xs font-medium text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors"
                          title="View and edit contacts"
                        >
                          <Users className="h-3 w-3 text-stone-400" />
                          <span>Contacts</span>
                        </button>

                        {/* Standard ActionMenu (Cleanly replaces duplicate pencil) */}
                        <ActionMenu
                          ariaLabel={`${batch.name} actions`}
                          items={[
                            {
                              label: "Assign campaign / agent",
                              onClick: () => openAssignModal(batch),
                            },
                            {
                              label: "Edit details & tags",
                              onClick: () => openEditModal(batch),
                            },
                            {
                              label: "View / Edit contacts",
                              onClick: () => navigate(`/crm/batches/${batch.batch_id}`),
                            },
                            {
                              label: "Schedule workflow",
                              onClick: () =>
                                navigate(`/home/automation/schedule?batchId=${encodeURIComponent(batch.batch_id)}`),
                            },
                            {
                              label: "Schedule WhatsApp campaign",
                              onClick: () =>
                                navigate(`/whatsapp/campaigns?batchId=${encodeURIComponent(batch.batch_id)}`),
                            },
                            {
                              label: "Delete batch",
                              danger: true,
                              onClick: () => setPendingDelete({ id: batch.batch_id, name: batch.name }),
                            },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Upload Contacts Modal */}
      {showUpload && <UploadContactBatchModal source="crm" onClose={() => setShowUpload(false)} />}

      {/* Edit Details & Tags Modal */}
      {editingBatch && (
        <Modal
          title="Edit batch"
          onClose={() => setEditingBatch(null)}
          width="max-w-sm"
          footer={
            <>
              <button
                type="button"
                onClick={() => setEditingBatch(null)}
                disabled={renameMutation.isPending}
                className="rounded-lg border border-line dark:border-white/15 px-4 py-2 text-sm font-semibold text-ink dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={!editName.trim() || renameMutation.isPending}
                className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
              >
                {renameMutation.isPending ? "Saving…" : "Save"}
              </button>
            </>
          }
        >
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              handleSaveEdit();
            }}
          >
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3 dark:text-stone-400">
                Batch name
              </span>
              <input
                autoFocus
                aria-label="Batch name"
                value={editName}
                maxLength={120}
                onChange={(e) => setEditName(e.target.value)}
                className="w-full rounded-lg border border-line dark:border-white/20 bg-stone-100 dark:bg-stone-800 px-3 py-2 text-sm text-ink dark:text-stone-100 focus:border-brand focus:outline-none"
              />
              {!editName.trim() && <span className="mt-1 block text-xs text-rose-500">Name is required.</span>}
            </label>
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3 dark:text-stone-400">
                Tags
              </span>
              <input
                aria-label="Tags"
                value={editTags}
                onChange={(e) => setEditTags(e.target.value)}
                placeholder="e.g. priority, diwali"
                className="w-full rounded-lg border border-line dark:border-white/20 bg-stone-100 dark:bg-stone-800 px-3 py-2 text-sm text-ink dark:text-stone-100 focus:border-brand focus:outline-none"
              />
              <span className="mt-1 block text-xs text-ink-3 dark:text-stone-400">Comma separated, up to 20 tags.</span>
            </label>
          </form>
        </Modal>
      )}

      {/* Assign Campaign / Agent Modal */}
      {assigningBatch && (
        <Modal
          title={`Assign "${assigningBatch.name}"`}
          onClose={() => setAssigningBatch(null)}
          width="max-w-sm"
          footer={
            <>
              <button
                type="button"
                onClick={() => setAssigningBatch(null)}
                disabled={assignMutation.isPending}
                className="rounded-lg border border-line dark:border-white/15 px-4 py-2 text-sm font-semibold text-ink dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveAssign}
                disabled={(!assignCampaignId && !assignAgentId) || assignMutation.isPending}
                className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
              >
                {assignMutation.isPending ? "Assigning…" : "Assign"}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <p className="text-xs text-ink-3 dark:text-stone-400">
              Applies to every lead created from this batch. Leave a field blank to leave it unchanged.
            </p>
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3 dark:text-stone-400">
                Campaign
              </span>
              <Dropdown
                ariaLabel="Campaign"
                value={assignCampaignId}
                onChange={setAssignCampaignId}
                className="w-full"
                options={[
                  { value: "", label: "Leave unchanged" },
                  ...campaigns.map((c) => ({ value: c.id, label: c.name })),
                ]}
              />
            </label>
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3 dark:text-stone-400">
                Agent
              </span>
              <Dropdown
                ariaLabel="Agent"
                value={assignAgentId}
                onChange={setAssignAgentId}
                className="w-full"
                options={[
                  { value: "", label: "Leave unchanged" },
                  ...(team?.members || []).filter((m) => m.active).map((m) => ({ value: m.id, label: m.name || m.email })),
                ]}
              />
            </label>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {pendingDelete && (
        <ConfirmModal
          title="Delete batch"
          body={`Delete batch "${pendingDelete.name}"? This removes it from Schedule Workflow too.`}
          confirmLabel={archiveMutation.isPending ? "Deleting…" : "Delete batch"}
          onConfirm={() => archiveMutation.mutate(pendingDelete.id)}
          onClose={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}
